/**
 * 도움말 워크플로 — AI 탭 안의 "도움말 서브에이전트".
 *
 * LLM 이 사용법을 한꺼번에 쏟아내면 읽히지 않는다. 그래서 사용법 대화는 **정해진 순서**로 진행한다:
 *   도움말 요청 → 메뉴(Quick Start / 주제별 안내 / 자유 질문) → 선택 → 단계별 진행(버튼으로 다음·그만)
 * 이 파일은 순수 함수다 — 상태와 입력을 받아 "보여 줄 메시지·다음 상태·해야 할 동작"을 돌려준다.
 * 실제 화면 반영(메시지 추가, 샘플 열기, LLM 호출)은 AssistantPanel 이 한다.
 *
 * Quick Start 는 사전에 정의한 트랙 셋(quickStartTracks.ts)이다 — 입문(SAM파일→DB) · 중급(분기·합류) · 고급(품질검사·위반 분리).
 * 어느 트랙이든 새 워크플로 → 노드·연결 → 저장 → 배포 → 실행 → 결과 확인 → 마무리로 끝난다. 각 단계는 직접 하거나 「대신 해줘」로 시킨다.
 * 「대신 해줘」는 LLM 을 거치지 않고 **미리 정한 도구 호출**(clientTools 와 같은 실행 경로)을 그대로 실행한다 —
 * 매번 같은 결과가 나와야 "따라 하기" 가 성립한다. 배포·실행도 사용자가 「대신 해줘」를 눌렀을 때만, 헤더 버튼과 같은 API 로 한다.
 *
 * [언어] 글은 언어별 문서(content/)에서 오고, **누른 버튼은 어느 언어의 글이든 알아본다** — 대화 중에 헤더에서
 * 언어를 바꿀 수 있기 때문이다. 판정(어느 버튼인가)은 글이 아니라 라벨 키로 한다(docs/I18N.md).
 *
 * 설계 원문: datamodeler docs/features/ai-chat/ai-help-agent.md — 상태기계 구조는 그대로, 단계만 이 앱 것.
 */
import { usageTopics, findTopic, UsageTopic } from './usageGuide';
import { quickTracks, QuickTrack, trackById, trackByLabel } from './quickStartTracks';
import { helpContent, allHelpContents, HelpContent } from './content';

export const HELP_STATE = {
  MENU: 'help_menu',
  QUICK_START: 'help_quick_start',
  TOPIC: 'help_topic',
} as const;

export interface HelpState {
  type: string;
  /** Quick Start 트랙 (basic | inter | adv) */
  track?: string;
  step?: number;
  topicId?: string;
  picking?: boolean | 'track';
}

export interface HelpMessage { text: string; options?: string[] }

/** 화면이 수행할 동작 — 도구 호출은 clientTools.runClientTool 과 같은 경로로 실행된다 */
export type HelpAction =
  | { type: 'tool'; name: string; input: Record<string, unknown> };

export interface HelpResult {
  messages: HelpMessage[];
  state: HelpState | null;
  actions?: HelpAction[];
  /** 있으면 상태를 유지한 채 **이 글만** LLM 에게 보낸다(사용자 메시지는 원문 그대로 표시) */
  agentText?: string;
  /** 손가락이 가리킬 곳 — null 이면 지운다, 없으면 그대로 둔다 (help/pointer) */
  point?: { target: string; label: string } | null;
}

type LabelKey = keyof HelpContent['labels'];

/** 지금 언어의 버튼 글 */
export const labels = () => helpContent().labels;

/** 누른 버튼이 그 라벨인가 — 어느 언어의 글이든 인정한다 */
const is = (msg: string, key: LabelKey): boolean => {
  const want = String(msg || '').trim();
  return allHelpContents().some((c) => c.labels[key] === want);
};

/** 도움말 명령어(「도움말」 / "help")를 어느 언어로 썼든 */
export const helpTriggerWords = () => allHelpContents().map((c) => c.triggers.help);

const fill = (text: string, values: Record<string, string | number>) =>
  text.replace(/\{\{(\w+)\}\}/g, (_, k) => String(values[k] ?? ''));

const menuMessage = (): HelpMessage => {
  const c = helpContent();
  return { text: c.ui.menu, options: [c.labels.quick, c.labels.topics, c.labels.free] };
};

const topicsMessage = (): HelpMessage => ({
  text: helpContent().ui.pickTopic,
  options: usageTopics().map((t) => t.title),
});

export const topicMessage = (t: UsageTopic): HelpMessage => {
  const c = helpContent();
  return {
    text: `${t.title}\n${t.summary}\n\n` + t.steps.map((s, i) => `${i + 1}. ${s}`).join('\n'),
    options: [...(t.sampleWorkflow ? [c.labels.sample] : []), c.labels.other, c.labels.free],
  };
};

const stepMessage = (t: QuickTrack, i: number): HelpMessage => {
  const s = t.steps[i];
  return { text: `${s.title}\n${s.text}`, options: s.options };
};

/** 트랙 고르기 — Quick Start 를 부르면 먼저 이것 */
const tracksMessage = (): HelpMessage => {
  const c = helpContent();
  return { text: c.ui.pickTrack, options: [...quickTracks(c).map((t) => t.label), c.labels.menu] };
};

const TRACK_WORD: Record<string, RegExp> = {
  basic: /입문|기본|초급|beginner|basic/i,
  inter: /중급|intermediate/i,
  adv: /고급|advanced/i,
};
const trackFromText = (msg: string): QuickTrack | null => {
  const hit = quickTracks().find((t) => TRACK_WORD[t.id].test(msg));
  return hit || trackByLabel(msg, allHelpContents());
};

const HELP_TRIGGER = /도움말|사용법|quick\s*start|퀵\s*스타트|빠른\s*시작|시작하기|처음이|처음인데|뭘 할 수|무엇을 할 수|어떻게 쓰|어떻게 사용|^help\b|\bhelp me\b|how (do i|to) use|getting started|what can i do/i;
const QUICK_RE = /quick\s*start|퀵\s*스타트|빠른\s*시작/i;
const ASK_RE = /어떻게|방법|설명해|알려줘|뭐야|무엇인|무엇이|how|what is|explain|guide/i;
const MENU_RE = /^(도움말|사용법|help|usage)$/i;
const STOP_RE = /^(그만|종료|중단|stop|quit|exit)$/i;
const NEXT_RE = /^(다음|넘어가|계속|next|continue)$/i;
const TOPICS_RE = /^2$|주제|topics?/i;
const FREE_RE = /^3$|질문|question|ask/i;

/**
 * 한 턴 판정.
 * @param state 현재 워크플로 상태 — 도움말 상태가 아니면 null 로 다룬다
 * @param text 사용자 메시지
 * @returns null 이면 이 워크플로가 다루지 않는다(LLM 으로). agentText 가 있으면 상태를 유지한 채 그 요청을 LLM 에게 보낸다.
 */
export function stepHelp(state: HelpState | null, text: string): HelpResult | null {
  const c = helpContent();
  const msg = String(text || '').trim();
  const type = state && String(state.type || '').startsWith('help_') ? state.type : null;
  const topics = usageTopics();

  // ── 상태 없음: 도움말 요청인가 ──
  if (!type) {
    const helpish = HELP_TRIGGER.test(msg) || ASK_RE.test(msg);
    // "분기는 어떻게 만들어?" 처럼 주제를 콕 집은 질문 — 그 주제 카드로
    const t = helpish ? matchTopic(msg) : null;
    if (t) return { messages: [topicMessage(t)], state: { type: HELP_STATE.TOPIC, topicId: t.id } };
    if (!HELP_TRIGGER.test(msg)) return null;
    if (QUICK_RE.test(msg)) return startQuick(trackFromText(msg));
    return { messages: [menuMessage()], state: { type: HELP_STATE.MENU } };
  }

  // 공통 제어 — 도움말 중에도 "quick start"/"도움말" 은 처음부터 다시
  if (QUICK_RE.test(msg)) return startQuick(trackFromText(msg));
  if (MENU_RE.test(msg)) return { messages: [menuMessage()], state: { type: HELP_STATE.MENU } };
  if (is(msg, 'stop') || STOP_RE.test(msg)) return { messages: [{ text: c.ui.stopped }], state: null };
  if (is(msg, 'menu')) return { messages: [menuMessage()], state: { type: HELP_STATE.MENU } };
  if (is(msg, 'free')) return { messages: [{ text: c.ui.freeAsk }], state: null };
  if (is(msg, 'topics')) return { messages: [topicsMessage()], state: { type: HELP_STATE.MENU, picking: true } };
  if (is(msg, 'quick') || is(msg, 'otherTrack')) return startQuick(null);
  // 트랙 버튼(입문/중급/고급)은 어느 상태에서든 그 트랙을 시작한다
  const picked = trackByLabel(msg, allHelpContents());
  if (picked) return startQuick(picked);

  // ── 메뉴 ──
  if (type === HELP_STATE.MENU) {
    const t = matchTopic(msg) || topics.find((x) => x.title === msg);
    if (t) return { messages: [topicMessage(t)], state: { type: HELP_STATE.TOPIC, topicId: t.id } };
    if (state!.picking === 'track' && trackFromText(msg)) return startQuick(trackFromText(msg));
    if (/^1$|quick/i.test(msg)) return startQuick(null);
    if (TOPICS_RE.test(msg)) return { messages: [topicsMessage()], state: { type: HELP_STATE.MENU, picking: true } };
    if (FREE_RE.test(msg)) return { messages: [{ text: c.ui.freeAskShort }], state: null };
    return null; // 메뉴와 무관한 말 — 상태를 버리고 LLM 으로
  }

  // ── 주제 카드 ──
  if (type === HELP_STATE.TOPIC) {
    const t = topics.find((x) => x.id === state!.topicId);
    if (is(msg, 'sample') && t && t.sampleWorkflow) {
      return {
        messages: [{ text: fill(c.ui.openingSample, { name: t.sampleWorkflow }) }],
        state: { type: HELP_STATE.TOPIC, topicId: t.id },
        actions: [{ type: 'tool', name: 'open_sample_workflow', input: { name: t.sampleWorkflow } }],
        agentText: fill(c.ui.explainSample, { name: t.sampleWorkflow, title: t.title, id: t.id }),
      };
    }
    if (is(msg, 'other')) return { messages: [topicsMessage()], state: { type: HELP_STATE.MENU, picking: true } };
    const t2 = matchTopic(msg) || topics.find((x) => x.title === msg);
    if (t2) return { messages: [topicMessage(t2)], state: { type: HELP_STATE.TOPIC, topicId: t2.id } };
    return null;
  }

  // ── Quick Start ──
  if (type === HELP_STATE.QUICK_START) {
    const track = trackById(state!.track) || quickTracks(c)[0];
    const i = state!.step || 0;
    const cur = track.steps[i];
    if (is(msg, 'next') || is(msg, 'did') || NEXT_RE.test(msg)) return gotoStep(track, i + 1);
    if (is(msg, 'doForMe') && cur && cur.doActions) {
      // 검증된 도구 호출을 그대로 실행한다 — 끝나면 사용자가 「다음」을 누른다
      return {
        messages: [{ text: cur.doneText || c.ui.doneDefault, options: [c.labels.next, c.labels.stop] }],
        state: { type: HELP_STATE.QUICK_START, track: track.id, step: i },
        actions: cur.doActions,
      };
    }
    if (is(msg, 'done')) return { messages: [{ text: c.ui.quickDone }], state: null };
    // 그 외의 말은 어시스턴트에게 — 단계는 유지한다(돌아와서 「다음」을 누를 수 있게)
    return { messages: [], state: { type: HELP_STATE.QUICK_START, track: track.id, step: i }, agentText: msg };
  }

  return null;
}

/** 트랙이 정해졌으면 그 트랙의 첫 단계, 아니면 트랙 고르기 */
function startQuick(track: QuickTrack | null): HelpResult {
  if (!track) return { messages: [tracksMessage()], state: { type: HELP_STATE.MENU, picking: 'track' } };
  return gotoStep(track, 0);
}

function gotoStep(track: QuickTrack, i: number): HelpResult {
  const c = helpContent();
  if (i >= track.steps.length) {
    return {
      messages: [{
        text: fill(c.ui.trackFinished, { track: track.label.split(' — ')[0] }),
        options: [c.labels.otherTrack, c.labels.topics, c.labels.free],
      }],
      state: { type: HELP_STATE.MENU },
    };
  }
  const s = track.steps[i];
  return {
    messages: [stepMessage(track, i)],
    state: { type: HELP_STATE.QUICK_START, track: track.id, step: i },
    ...(s.action ? { actions: [s.action] } : {}),
    point: s.point ?? null,
  };
}

/** 메시지에 주제 제목의 앞말이 들어 있으면 그 주제 (예: "품질검사 사용법 알려줘") */
export function matchTopic(msg: string): UsageTopic | null {
  const topics = usageTopics();
  const exact = topics.find((t) => t.title === msg);
  if (exact) return exact;
  const lower = msg.toLowerCase();
  const hit = topics.filter((t) => {
    // '데이터 정의 (SAM파일·DB테이블)' → '데이터' / 'Canvas and the DFD rules' → 'canvas'
    const key = t.title.replace(/\s*\(.*\)$/, '').split(/[·와과, ]/)[0].toLowerCase();
    return key.length >= 2 && lower.includes(key);
  });
  if (hit.length === 1) return hit[0];
  if (hit.length > 1) return findTopic(msg) || hit[0];
  return null;
}

export default stepHelp;
